import { useState, useEffect } from 'react';
import {
  DollarSign,
  CreditCard,
  Calendar,
  Timer,
  ArrowRight,
  TrendingUp,
  Zap,
  Clock,
  ShoppingCart,
} from 'lucide-react';
import { platformFetch } from '../../platform/http';

interface Milestone {
  milestone: number;
  reachedAt: string;
  daysFromPrevious: number;
  daysFromStart: number;
  daysPer500: number;
}

interface Prediction {
  milestone: number;
  predictedAt: string;
  daysFromNow: number;
  model: string;
}

interface RevenueMilestonesData {
  milestones: Milestone[];
  predictions: Prediction[];
  predictionModel: string;
  daysPer500: number;
  avgDaysPer500: number;
  recent30dDailyRevenue: number;
  recent7dDailyRevenue: number;
  totalRevenue: number;
  nextMilestone: number;
  orderCount: number;
  startDate: string | null;
}

function modelLabel(model: string): string {
  if (model === 'weighted_blend') return '基于7天/30天加权融合';
  if (model === 'recent_30d') return '基于最近30天日均收入';
  if (model === 'milestone_avg') return '基于里程碑平均增速';
  return '';
}

function speedLabel(daysPer500: number): string {
  if (daysPer500 <= 7) return '很快';
  if (daysPer500 <= 14) return '较快';
  if (daysPer500 <= 30) return '一般';
  if (daysPer500 <= 60) return '较慢';
  return '很慢';
}

function speedColor(daysPer500: number): string {
  if (daysPer500 <= 7) return 'text-emerald-600';
  if (daysPer500 <= 14) return 'text-teal-600';
  if (daysPer500 <= 30) return 'text-amber-600';
  return 'text-red-500';
}

function formatCurrency(value: number): string {
  return `¥${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function RevenueMilestonesTab() {
  const [data, setData] = useState<RevenueMilestonesData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await platformFetch('/admin/revenue-milestones');
        if (res.ok) {
          const json = await res.json();
          setData(json);
        } else {
          console.error('获取收入里程碑失败');
        }
      } catch {
        console.error('获取收入里程碑失败');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue" />
      </div>
    );
  }

  if (!data || data.milestones.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-light-border text-center">
        <DollarSign className="w-12 h-12 text-light-muted mx-auto mb-3" />
        <p className="text-light-muted">暂无收入里程碑数据（累计收入不足 ¥500）</p>
      </div>
    );
  }

  const progressToNext = data.totalRevenue % 500;
  const progressPercent = (progressToNext / 500) * 100;
  const firstPrediction = data.predictions[0];

  return (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-xs lg:text-sm">累计收入</p>
              <p className="text-2xl lg:text-3xl font-bold text-light-text mt-1">
                {formatCurrency(data.totalRevenue)}
              </p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 ml-3">
              <DollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-xs lg:text-sm">已支付订单</p>
              <p className="text-2xl lg:text-3xl font-bold text-light-text mt-1">
                {data.orderCount.toLocaleString()}
              </p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-blue-500 flex items-center justify-center shrink-0 ml-3">
              <ShoppingCart className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-xs lg:text-sm">已达成里程碑</p>
              <p className="text-2xl lg:text-3xl font-bold text-light-text mt-1">
                {data.milestones.length}
              </p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-amber-500 flex items-center justify-center shrink-0 ml-3">
              <CreditCard className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-light-muted text-xs lg:text-sm">
                距 {formatCurrency(data.nextMilestone)} 还差
              </p>
              <p className="text-2xl lg:text-3xl font-bold text-light-text mt-1">
                {formatCurrency(data.nextMilestone - data.totalRevenue)}
              </p>
              <div className="mt-2 w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 预测统计卡片 */}
      {firstPrediction && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-muted text-xs lg:text-sm">
                  预计到达 {formatCurrency(data.nextMilestone)}
                </p>
                <p className="text-xl lg:text-2xl font-bold text-accent-blue mt-1">
                  {firstPrediction.predictedAt}
                </p>
                <p className="text-xs text-light-muted mt-1">
                  约 {firstPrediction.daysFromNow} 天后
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-violet-500 flex items-center justify-center shrink-0 ml-3">
                <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-muted text-xs lg:text-sm">当前增速（每 ¥500）</p>
                <p className="text-xl lg:text-2xl font-bold text-light-text mt-1">
                  <span className={speedColor(data.daysPer500)}>{data.daysPer500}</span>
                  <span className="text-sm font-normal text-light-muted ml-1">天</span>
                </p>
                <p className={`text-xs mt-1 ${speedColor(data.daysPer500)}`}>
                  增速{speedLabel(data.daysPer500)}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-rose-500 flex items-center justify-center shrink-0 ml-3">
                <Zap className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-muted text-xs lg:text-sm">预测基准</p>
                <p className="text-sm font-medium text-light-text mt-1">
                  {modelLabel(data.predictionModel)}
                </p>
                <p className="text-xs text-light-muted mt-1">
                  里程碑平均 {data.avgDaysPer500} 天/500元 · 近7天日均 ¥{data.recent7dDailyRevenue} · 近30天日均 ¥{data.recent30dDailyRevenue}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-sky-500 flex items-center justify-center shrink-0 ml-3">
                <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 时间轴 */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-light-border">
        <h3 className="text-base font-semibold text-light-text mb-6 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-accent-blue" />
          收入增长里程碑时间轴
          {data.startDate && (
            <span className="text-xs font-normal text-light-muted ml-2">
              （始于 {data.startDate}）
            </span>
          )}
        </h3>

        <div className="relative">
          {/* 竖线 */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

          <div className="space-y-6">
            {data.milestones.map((m, index) => {
              const isFirst = index === 0;
              const isLast = index === data.milestones.length - 1;
              const prevMilestone = data.milestones[index - 1];
              const speedTrend =
                prevMilestone && m.daysPer500 < prevMilestone.daysPer500
                  ? '加速'
                  : prevMilestone && m.daysPer500 > prevMilestone.daysPer500
                    ? '放缓'
                    : null;

              return (
                <div key={m.milestone} className="relative flex items-start gap-4 pl-1">
                  {/* 节点圆点 */}
                  <div
                    className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                      isLast
                        ? 'bg-accent-blue text-white shadow-lg shadow-blue-200'
                        : 'bg-white border-2 border-gray-200 text-light-muted'
                    }`}
                  >
                    <span className="text-xs font-bold">
                      {m.milestone >= 1000 ? `${(m.milestone / 1000).toFixed(1)}k` : m.milestone}
                    </span>
                  </div>

                  {/* 内容卡片 */}
                  <div className="flex-1 bg-gray-50 rounded-xl p-4 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-light-muted" />
                        <span className="text-sm font-medium text-light-text">
                          {m.reachedAt}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Timer className="w-4 h-4 text-light-muted" />
                        <span className="text-sm text-light-text">
                          累计{' '}
                          <span className="font-semibold text-emerald-600">
                            {m.daysFromStart}
                          </span>{' '}
                          天
                        </span>
                      </div>

                      {!isFirst && (
                        <div className="flex items-center gap-1.5">
                          <ArrowRight className="w-4 h-4 text-light-muted" />
                          <span className="text-sm text-light-text">
                            <span className="font-semibold text-accent-blue">
                              {m.daysFromPrevious}
                            </span>{' '}
                            天
                          </span>
                          <span className="text-xs text-light-muted">
                            （距上一里程碑）
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-light-muted" />
                        <span className="text-sm text-light-text">
                          <span className={`font-semibold ${speedColor(m.daysPer500)}`}>
                            {m.daysPer500}
                          </span>{' '}
                          天/500元
                        </span>
                      </div>
                    </div>

                    {isFirst && (
                      <p className="text-xs text-light-muted mt-1">
                        从 ¥0 到 {formatCurrency(m.milestone)}，增速
                        <span className={`ml-1 font-medium ${speedColor(m.daysPer500)}`}>
                          {speedLabel(m.daysPer500)}
                        </span>
                      </p>
                    )}
                    {!isFirst && (
                      <p className="text-xs text-light-muted mt-1">
                        从 {formatCurrency(m.milestone - 500)} 到 {formatCurrency(m.milestone)}，增速
                        <span className={`ml-1 font-medium ${speedColor(m.daysPer500)}`}>
                          {speedLabel(m.daysPer500)}
                        </span>
                        {speedTrend && (
                          <span
                            className={`ml-1 font-medium ${
                              speedTrend === '加速' ? 'text-emerald-600' : 'text-red-500'
                            }`}
                          >
                            （{speedTrend}）
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* 预测里程碑节点 */}
            {data.predictions.map((p) => (
              <div
                key={p.milestone}
                className="relative flex items-start gap-4 pl-1 opacity-60"
              >
                <div className="relative z-10 w-12 h-12 rounded-full border-2 border-dashed border-violet-300 flex items-center justify-center shrink-0 bg-white">
                  <span className="text-xs font-bold text-violet-400">
                    {p.milestone >= 1000 ? `${(p.milestone / 1000).toFixed(1)}k` : p.milestone}
                  </span>
                </div>
                <div className="flex-1 border border-dashed border-violet-200 rounded-xl p-4 min-w-0 bg-violet-50/30">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-violet-400" />
                      <span className="text-sm font-medium text-violet-700">
                        预计 {p.predictedAt}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-violet-400" />
                      <span className="text-sm text-violet-600">
                        约{' '}
                        <span className="font-semibold">{p.daysFromNow}</span> 天后
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-violet-400 mt-1">
                    基于{modelLabel(p.model)}预测
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
